import styled from "styled-components";
import Button from "react-bootstrap/Button";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";

import { Fields, FilterCriteria } from "./types";
import FilterChip from "./FilterChip";
import FilterForm from "./FilterForm";
import {
  Toolbar,
  FixedToolbarItem,
  FlexToolbarItem,
  FieldType,
} from "@bagel-web/components";
import { useState } from "react";

const FilterChipContainer = styled(FlexToolbarItem)`
  display: flex;
  flex-flow: row wrap;
  gap: 10px;
`;

const StyledPopover = styled(Popover)`
  max-width: fit-content;
`;

const FilterBar = ({
  fields,
  criteria,
  onClear,
  onSetFilters,
  showChips = true,
}: {
  fields: Fields;
  criteria: FilterCriteria<string>;
  onClear: (fieldName: string) => void;
  onSetFilters: (criteria: FilterCriteria<string>) => void;
  showChips?: boolean;
}) => {
  const [showPopover, setShowPopover] = useState(false);

  const numVisibleFilters = fields.filter(
    (field) => !field.hidden && Object.hasOwn(criteria, field.name)
  ).length;

  const popover = (
    <StyledPopover>
      <Popover.Body>
        <FilterForm
          fields={fields}
          criteria={criteria}
          onSetFilters={(updatedCriteria) => {
            onSetFilters(updatedCriteria);
            setShowPopover(false);
          }}
        />
      </Popover.Body>
    </StyledPopover>
  );

  return (
    <Toolbar>
      {showChips && (
        <FilterChipContainer>
          {fields
            .filter((field) => !field.hidden && criteria[field.name])
            .flatMap((field) => {
              const value = criteria[field.name]?.value;
              const parts =
                (field.splitValue && field.splitValue(value || "")) || [];
              if (parts.length === 0) {
                return [
                  <FilterChip
                    key={field.name}
                    showValue={field.type !== FieldType.Boolean}
                    onClick={() => !field.required && onClear(field.name)}
                    field={field}
                    value={value}
                    required={field.required}
                  />,
                ];
              }
              // One chip per part; removing a chip drops only its part and
              // rebuilds the criterion from the rest. Keyed by part value
              // (plus occurrence number for duplicates) rather than index so
              // a removal unmounts the removed chip instead of shifting
              // parts onto reused chips with stale async formatValue state.
              const occurrences = new Map<string, number>();
              return parts.map((part, index) => {
                const occurrence = (occurrences.get(part) ?? 0) + 1;
                occurrences.set(part, occurrence);
                return (
                  <FilterChip
                    key={`${field.name}-${part}-${occurrence}`}
                    showValue={field.type !== FieldType.Boolean}
                    onClick={() => {
                      if (field.required) {
                        return;
                      }
                      const remaining = parts.filter((_, i) => i !== index);
                      if (remaining.length === 0) {
                        onClear(field.name);
                      } else {
                        onSetFilters({
                          ...criteria,
                          [field.name]: {
                            value: field.joinValue
                              ? field.joinValue(remaining)
                              : remaining.join(" "),
                          },
                        });
                      }
                    }}
                    field={field}
                    value={part}
                    required={field.required}
                  />
                );
              });
            })}
        </FilterChipContainer>
      )}
      <FixedToolbarItem>
        <OverlayTrigger
          trigger="click"
          rootClose
          placement="left"
          overlay={popover}
          show={showPopover}
          onToggle={setShowPopover}
        >
          <Button variant={numVisibleFilters > 0 ? "primary" : "secondary"}>
            <span>
              <i className="bi-filter" />{" "}
              {numVisibleFilters > 0
                ? `Filters (${numVisibleFilters})`
                : "Filter"}
            </span>
          </Button>
        </OverlayTrigger>
      </FixedToolbarItem>
    </Toolbar>
  );
};

export default FilterBar;
